import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type TeamProfileJSON = z.infer<typeof TeamProfileSchema.TeamProfileJSONZ>;
export type TeamProfileParams = z.infer<typeof TeamProfileSchema.TeamProfileZ>;

export default class TeamProfileSchema {
  public static readonly TeamProfileJSONZ = z.object({
    name: z.string(),
    description: z.string().nullish(),
    contactUserId: z.uuid().nullish(),
    categories: z.array(z.string()).nullish(),
    avatarId: z.uuid().nullish(),
  });
  public static readonly TeamProfileZ = z.object({
    name: z.string(),
    description: z.string().optional(),
    contactUserId: z.instanceof(Guid).optional(),
    categories: z.array(z.string()).default([]).optional(),
    avatarId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): TeamProfileParams {
    return parseOrThrow(TeamProfileSchema.TeamProfileZ, values, {
      logError: true,
      schemaName: "TeamProfileSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): TeamProfileJSON {
    return parseOrThrow(TeamProfileSchema.TeamProfileJSONZ, values, {
      logError: true,
      schemaName: "TeamProfileSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is TeamProfileParams {
    try {
      TeamProfileSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is TeamProfileJSON {
    try {
      TeamProfileSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: TeamProfileJSON): TeamProfileParams {
    const parsed = parseOrThrow(TeamProfileSchema.TeamProfileJSONZ, data, {
      logError: true,
      schemaName: "TeamProfileSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return TeamProfileSchema.codec.decode(parsed);
  }

  public static serialize(document: TeamProfileParams): TeamProfileJSON {
    const parsed = TeamProfileSchema.validate(document);
    return TeamProfileSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(TeamProfileSchema.TeamProfileJSONZ, TeamProfileSchema.TeamProfileZ, {
    decode: (data) => ({
      name: data.name,
      description: data.description ?? undefined,
      contactUserId: Guid.fromJSON(data.contactUserId) ?? undefined,
      categories: data.categories?.map((category) => category),
      avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
    }),
    encode: (teamProfile) => ({
      name: teamProfile.name,
      description: teamProfile.description,
      contactUserId: teamProfile.contactUserId?.toJSON(),
      categories: teamProfile.categories?.map((category) => category) ?? [],
      avatarId: teamProfile.avatarId?.toJSON(),
    }),
  });
}
